// =====================================================================
//  SUGERIR EL PRODUCTO DE UNA LÍNEA LEÍDA  (Ingresos por foto, D189)
// =====================================================================
//  «IP 15 (New) - 128GB - Black / IP15-128-BLK» es, a la vista, el «iPhone 15
//  128gb Black». Esto lo deduce sin que nadie lo explique: separa lo leído en
//  palabras, traduce las abreviaturas de los remitos (IP → iPhone, BLK →
//  Black, ULT → Ultramarine, 128 → 128 GB) y compara contra el NOMBRE de cada
//  producto del catálogo.
//
//  Sólo propone: la pantalla deja la sugerencia elegida y el usuario la
//  confirma (o elige otra). Nada se guarda sin «Confirmar», y la base valida
//  igual que siempre (D189).
//
//  Las reglas, de más dura a más blanda:
//   · Lo que identifica al equipo (la familia —iPhone, iPad…—, los números,
//     la capacidad y las palabras de versión —Pro, Max, Plus…—) si está en lo
//     leído TIENE que estar en el producto. Y una versión del producto que no
//     está en lo leído lo descarta: «iPhone 16» nunca es el «16 Pro».
//   · El color, si los dos lo dicen, tiene que coincidir (Blue y Ultramarine
//     se aceptan como parecidos, con aviso).
//   · Activado, Open Box, usado, + Cell: si uno lo dice y el otro no, resta.
//   · SIM: si el producto es «SIM + eSIM» o «Dual Sim» y lo leído no lo dice,
//     resta poco y avisa (es la duda típica del remito).
//  Si hay otro producto casi igual de parecido, se avisa que hay duda.

export type ProdSug = { codigo: string; nombre: string; nro_modelo: string | null };
export type Sugerencia<P extends ProdSug = ProdSug> = {
  producto: P;
  avisos: string[];     // lo que conviene mirar antes de confirmar
  dudosa: boolean;      // hay otro casi igual de parecido
  otros: P[];           // los siguientes más parecidos (para elegir con un clic)
};

const FAMILIA: Record<string, string> = {
  IP: 'IPHONE', IPH: 'IPHONE', IPHONE: 'IPHONE',
  IPAD: 'IPAD', IPD: 'IPAD',
  AIRPODS: 'AIRPODS', AIRPOD: 'AIRPODS', AIRP: 'AIRPODS',
  WATCH: 'WATCH', IWATCH: 'WATCH', AW: 'WATCH',
  MACBOOK: 'MACBOOK', MBA: 'MACBOOK', MBP: 'MACBOOK', IMAC: 'IMAC', MAC: 'MAC',
  KINDLE: 'KINDLE', SAMSUNG: 'SAMSUNG', GALAXY: 'SAMSUNG', XIAOMI: 'XIAOMI', REDMI: 'REDMI',
  PENCIL: 'PENCIL', MAGIC: 'MAGIC', AIRTAG: 'AIRTAG', HOMEPOD: 'HOMEPOD',
};
const VERSION = new Set(['PRO', 'MAX', 'PLUS', 'MINI', 'AIR', 'ULTRA', 'SE', 'NOTE', 'FOLIO']);
const COLOR: Record<string, string> = {
  BLACK: 'BLACK', BLK: 'BLACK', BK: 'BLACK', NEGRO: 'BLACK', NEG: 'BLACK',
  WHITE: 'WHITE', WHT: 'WHITE', WH: 'WHITE', BLANCO: 'WHITE',
  BLUE: 'BLUE', BLU: 'BLUE', BL: 'BLUE', AZUL: 'BLUE',
  PINK: 'PINK', PNK: 'PINK', PK: 'PINK', ROSA: 'PINK',
  GREEN: 'GREEN', GRN: 'GREEN', GR: 'GREEN', VERDE: 'GREEN',
  YELLOW: 'YELLOW', YLW: 'YELLOW', YEL: 'YELLOW', AMARILLO: 'YELLOW',
  PURPLE: 'PURPLE', PRP: 'PURPLE', PUR: 'PURPLE', VIOLETA: 'PURPLE', VIOLET: 'VIOLET',
  ULTRAMARINE: 'ULTRAMARINE', ULT: 'ULTRAMARINE', ULTRAM: 'ULTRAMARINE',
  TEAL: 'TEAL', TEA: 'TEAL',
  SILVER: 'SILVER', SLV: 'SILVER', SIL: 'SILVER', PLATA: 'SILVER', PLATEADO: 'SILVER',
  GOLD: 'GOLD', GLD: 'GOLD', DORADO: 'GOLD',
  GRAY: 'GRAY', GREY: 'GRAY', GRY: 'GRAY', GRIS: 'GRAY',
  STARLIGHT: 'STARLIGHT', STL: 'STARLIGHT', STAR: 'STARLIGHT',
  MIDNIGHT: 'MIDNIGHT', MID: 'MIDNIGHT', MDN: 'MIDNIGHT',
  NATURAL: 'NATURAL', NAT: 'NATURAL', DESERT: 'DESERT', DST: 'DESERT',
  RED: 'RED', ROJO: 'RED', ORANGE: 'ORANGE', NARANJA: 'ORANGE',
  LAVENDER: 'LAVENDER', LAV: 'LAVENDER', SAGE: 'SAGE', MIST: 'MIST', SKY: 'SKY',
  SPACE: 'SPACE', JET: 'JET', ROSE: 'ROSE', CORAL: 'CORAL', CREAM: 'CREAM', JADE: 'JADE',
  BRONZE: 'BRONZE', OLIVE: 'OLIVE', BURGUNDY: 'BURGUNDY', SAND: 'SAND', COSMIC: 'COSMIC',
};
//  Colores distintos que los proveedores usan como el mismo (el «Blue» del
//  iPhone 16 es el Ultramarine).
const PARECIDOS: [string, string][] = [['BLUE', 'ULTRAMARINE'], ['PURPLE', 'VIOLET'], ['PURPLE', 'LAVENDER']];
const ESTADO: Record<string, string> = {
  ACTIVATED: 'ACTIVADO', ACTIVADO: 'ACTIVADO', ACT: 'ACTIVADO',
  OPEN: 'OPEN', OPENBOX: 'OPEN', USED: 'USADO', USADO: 'USADO',
  REFURBISHED: 'REFURB', REFURB: 'REFURB', CPO: 'REFURB', DEMO: 'DEMO',
  CELL: 'CELL', CELLULAR: 'CELL', LTE: 'CELL',
};
const SIM = new Set(['SIM', 'ESIM', 'FISICO', 'DUAL', 'CHIP']);
const RUIDO = new Set(['NEW', 'NUEVO', 'NUEVA', 'APPLE', 'GB', 'TB', 'MM', 'CON', 'DE', 'Y', 'THE', 'WITH', 'AND', 'BOX', 'LL', 'UNLOCKED', 'LIBERADO', 'GEN']);
const CAPACIDADES = new Set([16, 32, 64, 128, 256, 512]);

type Rasgos = { fam: Set<string>; ver: Set<string>; num: Set<string>; cap: Set<string>; col: Set<string>; est: Set<string>; sim: Set<string>; otras: Set<string> };

const plano = (t: string) => t.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

//  «IP15-128GB-BLK» → IP · 15 · 128GB · BLK. Une «128 GB» y «42 mm» con su
//  unidad; los números sueltos de capacidad típica (128, 256…) en un código
//  de proveedor también cuentan como capacidad.
function palabras(t: string): string[]{
  const crudo = plano(t).replace(/(\d)\s+(GB|TB|MM)\b/g, '$1$2')
    .split(/[^A-Z0-9]+/).filter(Boolean)
    .flatMap(w => /^\d+(GB|TB|MM)$/.test(w) ? [w] : (w.match(/[A-Z]+|\d+/g) ?? []));
  return crudo;
}

function rasgos(t: string, codigo = false): Rasgos {
  const r: Rasgos = { fam: new Set(), ver: new Set(), num: new Set(), cap: new Set(), col: new Set(), est: new Set(), sim: new Set(), otras: new Set() };
  for (const w of palabras(t)){
    let m: RegExpMatchArray | null;
    if ((m = w.match(/^(\d+)(GB|TB)$/))) r.cap.add(m[2] === 'TB' ? String(Number(m[1]) * 1024) : m[1]);
    else if ((m = w.match(/^(\d+)MM$/))) r.num.add(m[1]);
    else if (/^\d+$/.test(w)) (codigo && CAPACIDADES.has(Number(w)) ? r.cap : r.num).add(String(Number(w)));
    else if (FAMILIA[w]) r.fam.add(FAMILIA[w]);
    else if (VERSION.has(w)) r.ver.add(w);
    else if (COLOR[w]) r.col.add(COLOR[w]);
    else if (ESTADO[w]) r.est.add(ESTADO[w]);
    else if (SIM.has(w)) r.sim.add(w);
    else if (!RUIDO.has(w) && w.length > 1) r.otras.add(w);
  }
  //  «iPhone 15 128» en una descripción: el 128 es la capacidad, no un modelo.
  if (!codigo && !r.cap.size) for (const n of [...r.num]) if (CAPACIDADES.has(Number(n)) && Number(n) >= 64){ r.num.delete(n); r.cap.add(n); }
  return r;
}

const une = (a: Rasgos, b: Rasgos): Rasgos => {
  const u = (x: Set<string>, y: Set<string>) => new Set([...x, ...y]);
  return { fam: u(a.fam, b.fam), ver: u(a.ver, b.ver), num: u(a.num, b.num), cap: u(a.cap, b.cap), col: u(a.col, b.col),
           est: u(a.est, b.est), sim: u(a.sim, b.sim), otras: u(a.otras, b.otras) };
};
const todas = (de: Set<string>, en: Set<string>) => [...de].every(x => en.has(x));
const alguna = (de: Set<string>, en: Set<string>) => [...de].some(x => en.has(x));
const parecido = (a: Set<string>, b: Set<string>) =>
  PARECIDOS.some(([x, y]) => (a.has(x) && b.has(y)) || (a.has(y) && b.has(x)));

type Puntaje = { s: number; avisos: string[] };

//  null = descartado.
function puntuar(l: Rasgos, lDesc: Rasgos, p: Rasgos): Puntaje | null {
  //  Lo duro sale de la descripción (un código de proveedor raro no descarta
  //  nada); si no hay descripción, del código.
  if (!todas(lDesc.fam, p.fam) || !todas(lDesc.ver, p.ver) || !todas(lDesc.num, p.num)) return null;
  if (l.cap.size && p.cap.size && !alguna(l.cap, p.cap)) return null;
  if (!todas(p.ver, l.ver)) return null;
  const avisos: string[] = [];
  let s = 0;
  s += 2 * [...l.fam].filter(x => p.fam.has(x)).length;
  s += 2 * [...l.ver].filter(x => p.ver.has(x)).length;
  s += 2 * [...l.num].filter(x => p.num.has(x)).length;
  s += 2 * [...l.cap].filter(x => p.cap.has(x)).length;
  s -= [...p.num].filter(x => !l.num.has(x)).length;
  s -= [...p.fam].filter(x => !l.fam.has(x)).length;
  if (l.cap.size && !p.cap.size) s -= 1;
  if (!l.cap.size && p.cap.size) s -= 0.5;
  if (l.col.size && p.col.size){
    if (alguna(l.col, p.col)) s += 2;
    else if (parecido(l.col, p.col)){ s += 1; avisos.push('el color leído no es igual al del catálogo, pero se usan como el mismo'); }
    else return null;
  } else if (l.col.size) s -= 1;
  const estFalta = [...p.est].filter(x => !l.est.has(x));
  const estSobra = [...l.est].filter(x => !p.est.has(x));
  s -= 3 * (estFalta.length + estSobra.length);
  if (estFalta.length) avisos.push('el producto del catálogo es ' + estFalta.map(e => NOMBRE_ESTADO[e] ?? e).join(', ') + ' y la foto no lo dice');
  if (p.sim.size && !l.sim.size){ s -= 1; avisos.push('la foto no dice si es SIM + eSIM o sólo eSIM: revisá'); }
  if (l.sim.size && !p.sim.size) s -= 2;
  s += 0.5 * [...l.otras].filter(x => p.otras.has(x)).length;
  s -= 0.25 * [...p.otras].filter(x => !l.otras.has(x)).length;
  return { s, avisos };
}
const NOMBRE_ESTADO: Record<string, string> = { ACTIVADO: 'activado', OPEN: 'open box', USADO: 'usado', REFURB: 'reacondicionado', DEMO: 'demo', CELL: '+ Cell' };

export function sugerir<P extends ProdSug>(descripcion: string, codigoProveedor: string | null, catalogo: P[]): Sugerencia<P> | null {
  const desc = (descripcion ?? '').trim();
  const cod = (codigoProveedor ?? '').trim();
  if (!catalogo.length || (!desc && !cod)) return null;
  //  El código leído igual a un código o # Modelo del catálogo: ése es.
  const exacto = (x: string) => plano(x).replace(/[^A-Z0-9]/g, '');
  if (cod){
    const c = exacto(cod);
    const p = catalogo.find(x => exacto(x.codigo) === c || (x.nro_modelo && exacto(x.nro_modelo) === c));
    if (p) return { producto: p, avisos: [], dudosa: false, otros: [] };
  }
  const lDesc = rasgos(desc || cod, !desc);
  const l = cod && desc ? une(lDesc, rasgos(cod, true)) : lDesc;
  //  Sin nada que identifique al equipo, no se arriesga.
  if (!l.fam.size && !l.num.size && !l.cap.size && !l.ver.size) return null;
  const puntos = catalogo
    .map(p => ({ p, r: puntuar(l, lDesc, rasgos(p.nombre)) }))
    .filter((x): x is { p: P; r: Puntaje } => x.r !== null && x.r.s >= 4)
    .sort((a, b) => b.r.s - a.r.s || a.p.nombre.length - b.p.nombre.length || a.p.codigo.localeCompare(b.p.codigo));
  if (!puntos.length) return null;
  const [mejor, ...resto] = puntos;
  const dudosa = resto.length > 0 && resto[0].r.s >= mejor.r.s - 1;
  const avisos = [...mejor.r.avisos];
  if (dudosa){
    const simMejor = rasgos(mejor.p.nombre).sim.size > 0, simOtro = rasgos(resto[0].p.nombre).sim.size > 0;
    if (simMejor !== simOtro) avisos.splice(0, avisos.length, ...avisos.filter(a => !a.includes('SIM')));
    avisos.push(simMejor !== simOtro
      ? `la foto no dice si es sólo eSIM o SIM + eSIM, y en el catálogo están los dos (el otro es ${resto[0].p.codigo}): revisá`
      : 'hay otro producto casi igual de parecido: mirá la lista de abajo');
  }
  return { producto: mejor.p, avisos, dudosa, otros: resto.slice(0, 4).map(x => x.p) };
}
